import type { ContainerConfig, HomeContainer } from "@ronin/contracts";
import { Input, Label } from "@ronin/ui";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useId, useState } from "react";
import { errorMessage } from "../../lib/errors.js";
import { containerStatus, containersQuery, StatusBadge } from "./container-widget.js";

/** spec14 FR5: a new card's settings from the container; the user changes them in the dialog. */
const containerDefaults = (container: HomeContainer): ContainerConfig => ({
  container: container.name,
  name: container.name.slice(0, 60),
  url: "",
  icon: { kind: "lucide", name: "container" },
});

/** spec14 FR4: this server's containers (name, image, state) with a search field. */
export function ContainerPicker({ onPick }: { onPick: (config: ContainerConfig) => void }) {
  const id = useId();
  const [search, setSearch] = useState("");
  const list = useQuery(containersQuery);
  const terms = search.trim().toLowerCase();
  const shown = (list.data ?? []).filter(
    (c) => c.name.toLowerCase().includes(terms) || c.image.toLowerCase().includes(terms),
  );

  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor={`${id}-search`} className="sr-only">
        Search containers
      </Label>
      <Input
        id={`${id}-search`}
        value={search}
        placeholder="Search containers"
        data-autofocus
        onChange={(e) => setSearch(e.target.value)}
      />
      {list.isPending && (
        <p className="flex items-center gap-2 text-xs text-dim-foreground">
          <Loader2 className="size-3 animate-spin" aria-hidden />
          Loading containers …
        </p>
      )}
      {list.isError && (
        <p className="text-xs text-destructive">
          {errorMessage(list.error, "the containers", "loaded")}
        </p>
      )}
      {list.data && shown.length === 0 && (
        <p className="text-xs text-muted-foreground">No container matches.</p>
      )}
      <ul className="flex max-h-72 flex-col gap-1 overflow-y-auto">
        {shown.map((container) => {
          const status = containerStatus(container);
          return (
            <li key={container.name}>
              <button
                type="button"
                onClick={() => onPick(containerDefaults(container))}
                className="flex w-full items-center gap-3 border bg-card px-3 py-2 text-left transition-colors duration-150 hover:bg-muted/40 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring disabled:opacity-50"
              >
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate text-sm font-medium text-foreground">
                    {container.name}
                  </span>
                  <span className="truncate text-xs text-muted-foreground">{container.image}</span>
                </span>
                <StatusBadge status={status} />
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
